import { attributionAnalyticsProjection } from "./logic/helpers/attributionAnalytics";
import { countryAnalyticsCache } from "./logic/helpers/countryAnalyticsContext";
// src/main.tsx

import { createRoot } from "react-dom/client";
import UserReason from "@userreason/js";
import { playerFeedbackMessages } from "./logic/helpers/playerFeedbackMessages";
import { BrowserRouter } from "react-router-dom";
import { TonConnectUIProvider } from "@tonconnect/ui-react";
import { TelegramAuthClient } from "@bridge-applications/tg-auth";
import {
    ApiAuthAccessTokenResSchema,
    ApiAuthAppSessionReqSchema,
    ApiAuthAppSessionResSchema,
    ApiAuthClearCookiesResSchema,
    ApiAuthSignOutReqSchema,
    ApiAuthSignOutResSchema,
} from "@bridge-applications/pb-types";
import App from "./App.tsx";
import {
    getTelegramWebApp,
    isTelegramMobilePlatform,
} from "./logic/helpers/telegram.ts";
import { SAFE_AREA_BACKDROP_COLOR } from "./state/theme.ts";
import {
    API_BASE,
    BUILD_TIME,
    CLIENT_VERSION,
    POSTHOG_ENABLED,
    TON_CONNECT_MANIFEST_URL,
    TON_CONNECT_TWA_RETURN_URL,
} from "./state/config.ts";
import i18n from "./logic/i18n/i18n.ts";
import { PostHogProvider } from "@posthog/react";
import type { PostHogConfig } from "posthog-js";
import { SlicesProvider } from "@bridge-applications/provider-kit";
import { Slices } from "./state/slices/Slices.ts";
import { checkMaintenance } from "./logic/helpers/maintenance.ts";
import MaintenancePage from "./components/pages/MaintenancePage/MaintenancePage.tsx";
import SafeAreaView from "./components/app/SafeAreaView/SafeAreaView.tsx";
import { readAccountAnalyticsIdentity } from "./logic/helpers/authIdentityStorage";
import { accountAnalyticsEventProperties } from "./logic/helpers/accountAnalytics";
import { CLIENT_ANALYTICS_SCHEMA_VERSION } from "./logic/helpers/analytics.ts";
import {
    beginNetworkAnalyticsSession,
    browserNetworkContextStorage,
    networkAnalyticsEventProperties,
} from "./logic/helpers/networkAnalyticsContext.ts";

UserReason.init({
    siteId: "3f66120f-736d-4381-8c12-2eb27c13d322",
    apiBaseUrl: "https://api-dev.userreason.com",
    waitForIdentity: true,
    locale: i18n.resolvedLanguage ?? i18n.language ?? "en",
    messages: playerFeedbackMessages(i18n.resolvedLanguage ?? i18n.language ?? "en"),
    appVersion: CLIENT_VERSION,
});

const _isDev = import.meta.env.VITE_NODE_ENV === "development";

if (_isDev) {
    console.log("[Build] Client version:", CLIENT_VERSION);
    console.log("[Build] Build time:", BUILD_TIME);
}

// ___tell Telegram the app is ready and take the full viewport height
const webApp = getTelegramWebApp();
const isBrowserDevelopment = _isDev && !webApp?.initData;
const networkContextStorage = browserNetworkContextStorage();
const telegramUserId = webApp?.initDataUnsafe.user?.id;
if (POSTHOG_ENABLED) {
    beginNetworkAnalyticsSession(networkContextStorage, telegramUserId);
    countryAnalyticsCache.begin(networkContextStorage, telegramUserId);
}
if (webApp) {
    webApp.ready();
    webApp.expand();

    // ___the header is transparent in fullscreen mode, so match Telegram's
    // own native chrome (status bar, header) to the safe-area backdrop
    // color used in globals.scss to avoid a visible mismatch/flash
    webApp.setHeaderColor(SAFE_AREA_BACKDROP_COLOR);
    webApp.setBackgroundColor(SAFE_AREA_BACKDROP_COLOR);

    // ___keep the portrait-first game UI in its launch orientation and use
    // true edge-to-edge fullscreen (Bot API 8.0+). Telegram's orientation
    // lock prevents later device rotation from switching the Mini App to
    // landscape; older clients remain expanded but cannot be locked.
    // Desktop and web clients would fill the whole monitor, so they keep
    // Telegram's regular Mini App window.
    if (webApp.isVersionAtLeast("8.0") && isTelegramMobilePlatform(webApp)) {
        webApp.lockOrientation();
        webApp.requestFullscreen();
    }
}

function promptForDevelopmentInitData(): string {
    const initData = window
        .prompt(i18n.t("auth.development.initDataPrompt"))
        ?.trim();
    if (!initData) {
        throw new Error(i18n.t("auth.development.initDataRequired"));
    }
    return initData;
}

TelegramAuthClient.init({
    baseUrl: API_BASE,
    clientVersion: CLIENT_VERSION,
    telegram: webApp ?? undefined,
    urlPaths: isBrowserDevelopment
        ? { appSession: "/api/auth/dev/app-session" }
        : undefined,
    initDataProvider: isBrowserDevelopment
        ? promptForDevelopmentInitData
        : undefined,
    customSchemas: {
        appSession: {
            req: ApiAuthAppSessionReqSchema,
            res: ApiAuthAppSessionResSchema,
        },
        accessToken: {
            res: ApiAuthAccessTokenResSchema,
        },
        signOut: {
            req: ApiAuthSignOutReqSchema,
            res: ApiAuthSignOutResSchema,
        },
        clearCookies: {
            res: ApiAuthClearCookiesResSchema,
        },
    },
    debug: _isDev,
});

const options: Partial<PostHogConfig> = {
    api_host: import.meta.env.VITE_POSTHOG_HOST, // ---cf reverse proxy
    ui_host: "https://eu.posthog.com",
    defaults: "2026-05-30",
    opt_out_capturing_by_default: !POSTHOG_ENABLED,
    opt_out_persistence_by_default: !POSTHOG_ENABLED,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: false,
    capture_heatmaps: false,
    capture_performance: false,
    capture_dead_clicks: false,
    disable_surveys: true,
    // Keep PostHog's automatic canonical pageview/pageleave behavior intact.
    // The final send hook adds only sanitized cached context and therefore
    // also enriches the initial pageview without delaying application startup.
    before_send: (event) => {
        if (!event) return event;
        return {
            ...event,
            properties: {
                ...event.properties,
                ...attributionAnalyticsProjection.properties(
                    readAccountAnalyticsIdentity(
                        getTelegramWebApp()?.initDataUnsafe.user?.id,
                    )?.userId,
                ),
                ...accountAnalyticsEventProperties(
                    readAccountAnalyticsIdentity(
                        getTelegramWebApp()?.initDataUnsafe.user?.id,
                    ),
                    event.timestamp ?? Date.now(),
                ),
                ...countryAnalyticsCache.properties(
                    getTelegramWebApp()?.initDataUnsafe.user?.id,
                ),
                ...networkAnalyticsEventProperties(
                    networkContextStorage,
                    telegramUserId,
                ),
            },
        };
    },
    // ___stamp every event with the build's git hash so client errors can
    // be attributed to a specific deploy (or to a stale cached bundle)
    loaded: (ph) => {
        if (!POSTHOG_ENABLED) return;
        ph.register({
            client_version: CLIENT_VERSION,
            environment: _isDev ? "development" : "production",
            event_schema_version: CLIENT_ANALYTICS_SCHEMA_VERSION,
            event_source: "pbtg-client",
        });
    },
};

const root = createRoot(document.getElementById("root")!);

function renderApplication(): void {
    root.render(
        <SlicesProvider slices={Slices}>
            <PostHogProvider
                apiKey={import.meta.env.VITE_POSTHOG_PROJECT_TOKEN}
                options={options}
            >
                <TonConnectUIProvider
                    manifestUrl={TON_CONNECT_MANIFEST_URL}
                    actionsConfiguration={{
                        twaReturnUrl: TON_CONNECT_TWA_RETURN_URL,
                    }}
                >
                    <BrowserRouter>
                        <App />
                    </BrowserRouter>
                </TonConnectUIProvider>
            </PostHogProvider>
        </SlicesProvider>,
    );
}

async function start(): Promise<void> {
    try {
        const maintenanceEnd = await checkMaintenance();
        if (maintenanceEnd !== null) {
            root.render(
                <SafeAreaView>
                    <MaintenancePage maintenanceEnd={maintenanceEnd} />
                </SafeAreaView>,
            );
            return;
        }
    } catch (error) {
        console.error(
            "[Maintenance] Status check failed, starting the application:",
            error,
        );
    }

    renderApplication();
}

void start();
